import { useEffect, useMemo, useRef } from "react";

import type { ReceivedGumball, ThemeSceneProps } from "../types";

const PATH_VIEW_W = 1000;
const PATH_VIEW_H = 600;
const RAINBOW_PATH =
  "M 1020 170 C 850 120, 720 230, 610 315 S 390 485, 245 385 S 90 250, -20 460";

const CARD_SPEED = 0.042;

const RAINBOW_BANDS: ReadonlyArray<{
  color: string;
  offset: number;
  width: number;
  opacity: number;
}> = [
  { color: "#ff4fd8", offset: -24, width: 6, opacity: 0.98 },
  { color: "#ff9d4d", offset: -16, width: 6, opacity: 0.94 },
  { color: "#ffe66d", offset: -8, width: 6, opacity: 0.94 },
  { color: "#95ff8f", offset: 0, width: 6, opacity: 0.92 },
  { color: "#75f6ff", offset: 8, width: 6, opacity: 0.96 },
  { color: "#9f7aff", offset: 16, width: 6, opacity: 0.95 },
  { color: "#ff4fd8", offset: 24, width: 6, opacity: 0.9 }
];

const GUMBALL_TINTS: ReadonlyArray<readonly [string, string, string]> = [
  ["#ff4fd8", "#ffe66d", "#ff9d4d"],
  ["#75f6ff", "#95ff8f", "#9f7aff"],
  ["#ffe66d", "#ff9d4d", "#ff4fd8"],
  ["#9f7aff", "#75f6ff", "#ff4fd8"],
  ["#95ff8f", "#75f6ff", "#ffe66d"]
];

const STARS = buildStars(130);
const CONFETTI = buildConfetti(34);

export function PrismCandylandScene({ received }: ThemeSceneProps) {
  const pathRef = useRef<SVGPathElement>(null);
  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);
  const cardTRef = useRef<number[]>([]);

  useEffect(() => {
    cardTRef.current = received.map((_, i) =>
      received.length === 0 ? 0 : (i / received.length + 0.08) % 1
    );
    cardRefs.current.length = received.length;
  }, [received.length]);

  useEffect(() => {
    const path = pathRef.current;
    if (path === null) {
      return;
    }
    let total = path.getTotalLength();
    let last = performance.now();
    let raf = 0;

    const onResize = () => {
      total = path.getTotalLength();
    };
    window.addEventListener("resize", onResize);

    const tick = (now: number) => {
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      const ts = cardTRef.current;
      for (let i = 0; i < ts.length; i++) {
        ts[i] = (ts[i]! + CARD_SPEED * dt) % 1;
        const point = path.getPointAtLength(ts[i]! * total);
        const el = cardRefs.current[i];
        if (el !== null && el !== undefined) {
          el.style.left = `${(point.x / PATH_VIEW_W) * 100}%`;
          el.style.top = `${(point.y / PATH_VIEW_H) * 100}%`;
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, [received.length]);

  return (
    <div className="pc-scene" aria-hidden="true">
      <div className="pc-scene__mesh" />
      <div className="pc-scene__grid" />
      <div className="pc-scene__stars">
        {STARS.map((star, i) => (
          <span
            key={i}
            className={`pc-scene__star pc-scene__star--${star.kind}`}
            style={{
              left: `${star.x}%`,
              top: `${star.y}%`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              backgroundColor: star.color,
              boxShadow: `0 0 ${Math.max(5, star.size * 2.8)}px ${star.color}`,
              animationDelay: `${star.delay}s`,
              animationDuration: `${star.duration}s`
            }}
          />
        ))}
      </div>
      <div className="pc-scene__confetti">
        {CONFETTI.map((piece, i) => (
          <span
            key={i}
            className={`pc-confetti pc-confetti--${piece.kind}`}
            style={{
              left: `${piece.x}%`,
              top: `${piece.y}%`,
              width: `${piece.w}px`,
              height: `${piece.h}px`,
              background: piece.background,
              color: piece.background,
              animationDelay: `${piece.delay}s`,
              animationDuration: `${piece.duration}s`
            }}
          />
        ))}
      </div>

      <CandylandHorizon />

      <svg
        className="pc-scene__rainbow"
        viewBox={`0 0 ${PATH_VIEW_W} ${PATH_VIEW_H}`}
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="pc-road-fill" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#130a31" />
            <stop offset="50%" stopColor="#2b114f" />
            <stop offset="100%" stopColor="#090716" />
          </linearGradient>
          <filter id="pc-road-glow" x="-12%" y="-60%" width="124%" height="220%">
            <feGaussianBlur stdDeviation="10" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <path
          d={RAINBOW_PATH}
          stroke="rgba(255, 79, 216, 0.28)"
          strokeWidth="96"
          fill="none"
          strokeLinecap="round"
          filter="url(#pc-road-glow)"
        />
        <path
          d={RAINBOW_PATH}
          stroke="url(#pc-road-fill)"
          strokeWidth="68"
          fill="none"
          strokeLinecap="round"
          opacity="0.9"
        />
        {RAINBOW_BANDS.map((band, i) => (
          <path
            key={i}
            d={RAINBOW_PATH}
            stroke={band.color}
            strokeWidth={band.width}
            fill="none"
            strokeLinecap="round"
            transform={`translate(0 ${band.offset})`}
            opacity={band.opacity}
          />
        ))}
        <path ref={pathRef} d={RAINBOW_PATH} stroke="none" fill="none" />
      </svg>

      <PrismUnicorn />

      <div className="pc-scene__cards">
        {received.map((entry, i) => (
          <GumballCard
            key={entry.id}
            entry={entry}
            index={i}
            elRef={(el) => {
              cardRefs.current[i] = el;
            }}
          />
        ))}
      </div>
    </div>
  );
}

function CandylandHorizon() {
  return (
    <svg
      className="pc-scene__horizon"
      viewBox="0 0 1000 600"
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id="pc-hill-back" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#75f6ff" stopOpacity="0.28" />
          <stop offset="100%" stopColor="#070611" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="pc-hill-mid" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ff4fd8" stopOpacity="0.34" />
          <stop offset="100%" stopColor="#120923" stopOpacity="0.05" />
        </linearGradient>
        <linearGradient id="pc-hill-front" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffe66d" stopOpacity="0.35" />
          <stop offset="38%" stopColor="#ff9d4d" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#070611" stopOpacity="0" />
        </linearGradient>
        <filter id="pc-hill-blur" x="-6%" y="-20%" width="112%" height="140%">
          <feGaussianBlur stdDeviation="4" />
        </filter>
      </defs>
      <path
        d="M0 438 C 170 365, 340 420, 510 390 S 820 450, 1000 390 L1000 600 L0 600 Z"
        fill="url(#pc-hill-back)"
        filter="url(#pc-hill-blur)"
      />
      <path
        d="M0 498 C 190 430, 350 470, 520 454 S 820 510, 1000 456 L1000 600 L0 600 Z"
        fill="url(#pc-hill-mid)"
      />
      <path
        d="M0 560 C 180 510, 335 535, 520 526 S 835 574, 1000 532 L1000 600 L0 600 Z"
        fill="url(#pc-hill-front)"
      />
    </svg>
  );
}

function PrismUnicorn() {
  return (
    <div className="pc-mascot" aria-hidden="true">
      <div className="pc-mascot__bob">
        <img
          className="pc-mascot__image"
          src="/assets/prism-candyland-unicorn.png"
          alt=""
          draggable={false}
        />
      </div>
    </div>
  );
}

function GumballCard({
  entry,
  index,
  elRef
}: {
  entry: ReceivedGumball;
  index: number;
  elRef: (el: HTMLDivElement | null) => void;
}) {
  const count = Math.max(1, Math.min(entry.amount, 9));
  const columns = Math.min(count, 3);
  const pieces = useMemo(() => Array.from({ length: count }, (_, i) => i), [count]);
  return (
    <div ref={elRef} className="pc-card">
      <div className="pc-card__bob" style={{ animationDelay: `${(index % 6) * -0.45}s` }}>
        <span className="pc-card__halo" />
        <div
          className="pc-card__cluster"
          style={{ gridTemplateColumns: `repeat(${columns}, 20px)` }}
        >
          {pieces.map((piece) => (
            <Gumball key={piece} variant={(index + piece) % GUMBALL_TINTS.length} index={index * 11 + piece} />
          ))}
        </div>
        <div className="pc-card__tooltip" role="tooltip">
          <span className="pc-card__from">{entry.from}</span>
          <span className="pc-card__amount">{entry.amount}</span>
          {entry.reason !== null && entry.reason.trim() !== "" && (
            <div className="pc-card__note">{entry.reason}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Gumball({ variant, index }: { variant: number; index: number }) {
  const [core, rim, glow] = GUMBALL_TINTS[variant % GUMBALL_TINTS.length]!;
  const id = `pc-gumball-${index}`;
  return (
    <svg className="pc-card__piece" width="20" height="20" viewBox="0 0 22 22" aria-hidden="true">
      <defs>
        <radialGradient id={id} cx="32%" cy="26%" r="75%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="26%" stopColor={core} />
          <stop offset="74%" stopColor={rim} />
          <stop offset="100%" stopColor={glow} />
        </radialGradient>
      </defs>
      <circle cx="11" cy="11" r="10" fill={`url(#${id})`} />
      <circle cx="11" cy="11" r="9.4" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="0.6" />
      <path d="M5 12 C8 9, 13 9, 17 12" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="0.7" />
      <circle cx="7.2" cy="6.5" r="2.3" fill="rgba(255,255,255,0.72)" />
    </svg>
  );
}

function buildStars(count: number) {
  const out: Array<{
    x: number;
    y: number;
    size: number;
    delay: number;
    duration: number;
    color: string;
    kind: "pin" | "flare" | "soft";
  }> = [];
  const colors = ["#ffffff", "#ffeffb", "#d9fbff", "#fff4b8", "#e7dcff"];
  let seed = 0x51a7;
  for (let i = 0; i < count; i++) {
    seed = nextSeed(seed);
    const x = (seed % 1000) / 10;
    seed = nextSeed(seed);
    const y = (seed % 760) / 10;
    seed = nextSeed(seed);
    const roll = seed % 100;
    const size = roll < 74 ? 1 + (roll % 14) / 10 : 2.4 + (roll % 26) / 10;
    seed = nextSeed(seed);
    const delay = -((seed % 80) / 10);
    seed = nextSeed(seed);
    const duration = 2.8 + (seed % 44) / 10;
    seed = nextSeed(seed);
    const color = colors[seed % colors.length]!;
    const kind = roll < 74 ? "pin" : roll < 92 ? "soft" : "flare";
    out.push({ x, y, size, delay, duration, color, kind });
  }
  return out;
}

function buildConfetti(count: number) {
  const out: Array<{
    x: number;
    y: number;
    w: number;
    h: number;
    delay: number;
    duration: number;
    background: string;
    kind: "pill" | "diamond";
  }> = [];
  const colors = ["#ff4fd8", "#ffe66d", "#75f6ff", "#95ff8f", "#9f7aff"];
  let seed = 0xca1d5;
  for (let i = 0; i < count; i++) {
    seed = nextSeed(seed);
    const x = (seed % 1000) / 10;
    seed = nextSeed(seed);
    const y = (seed % 680) / 10;
    seed = nextSeed(seed);
    const w = 3 + (seed % 34) / 10;
    seed = nextSeed(seed);
    const h = 7 + (seed % 54) / 10;
    seed = nextSeed(seed);
    const delay = -((seed % 100) / 10);
    seed = nextSeed(seed);
    const duration = 6 + (seed % 80) / 10;
    seed = nextSeed(seed);
    const background = colors[seed % colors.length]!;
    const kind = i % 3 === 0 ? "diamond" : "pill";
    out.push({ x, y, w, h, delay, duration, background, kind });
  }
  return out;
}

function nextSeed(seed: number): number {
  return (seed * 1103515245 + 12345) & 0x7fffffff;
}
